import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import {
  User,
  UserSettings,
  Subscription,
  BlocklistEntry,
  Report,
  ReportArticle,
  TokenResponse,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
  BatchSubscriptionRequest,
  CreateBlocklistRequest,
  UpdateBlocklistRequest,
  GenerateReportRequest,
  CreateWebhookRequest,
  UpdateWebhookRequest,
  WebhookTestResult,
  ApiResponse,
  ListResponse,
  DashboardStats,
  UserStats,
  UserReport,
  SubscriptionGroup,
  SubscriptionArticlesResponse,
  ArticleSummaryRequest,
  FeedArticlesResponse,
  Prompt,
  PromptType,
  CreatePromptRequest,
  UpdatePromptRequest,
  UserTag,
  CreateUserTagRequest,
  UpdateUserTagRequest,
  AIGenerateBlockRequest,
  AIGenerateBlockResponse,
  ApplyBlockTagsRequest,
  FeedPreviewRequest,
  FeedPreviewResponse,
  FeedDiscoveryRequest,
  FeedDiscoveryResponse,
  DiscoverFeedsRequest,
  DiscoverFeedsResponse,
  ValidateFeedRequest,
  ValidateFeedResponse,
  FeedInfo,
  OPMLImportResponse,
  UnreadCountsResponse,
  ArticleContentResponse,
  TrendingTagsResponse,
  ContentTypeStatsResponse,
  DuplicatesResponse,
  MarkDuplicatesRequest,
  MarkDuplicatesResponse,
  Tag,
  CreateTagRequest,
  UpdateTagRequest,
  AddTagToArticleRequest,
  TagsListResponse,
  ArticleTagsResponse,
  SearchArticlesByTagsResponse,
  TagsForFilterResponse,
  NotificationSubscription,
  NotificationTemplateItem,
  NotificationDomainItem,
  NotificationPreviewResponse,
} from './types';
import { getToken, removeToken, removeUser, removeRefreshToken } from '../utils/localStorage';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// 创建 axios 实例
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  withCredentials: true, // 自动携带 httpOnly cookie
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 优先使用 httpOnly cookie（自动携带），回退到 localStorage token（向后兼容）
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// 响应拦截器 - 处理401错误（清除本地状态，cookie 由后端在 /api/auth/logout 时清除）
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // 跳过 auth 相关请求（AuthContext 自行处理 401）
      const isAuthEndpoint = url.includes('/api/auth/');
      if (!isAuthEndpoint) {
        removeToken();
        removeRefreshToken();
        removeUser();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

// ==================== 认证 API ====================
export const authApi = {
  // 获取当前用户（后端直接返回 User，兼容两种格式）
  getMe: () => api.get<User & { data?: User }>('/api/auth/me'),

  // 更新用户信息
  updateMe: (data: Partial<User>) => api.put<ApiResponse<User>>('/api/auth/me', data),

  // 登出
  logout: () => api.post<ApiResponse<unknown>>('/api/auth/logout'),

  // 请求验证码
  requestCode: (email: string) =>
    api.post<{ success: boolean; message: string }>('/api/auth/request-code', { email }),

  // 验证码登录
  verifyCode: (email: string, code: string) =>
    api.post<{
      access_token: string;
      refresh_token?: string;
      token_type: string;
      expires_in: number;
      user: User;
    }>('/api/auth/verify-code', { email, code }),

  // 刷新 token
  refreshToken: (refreshToken: string) =>
    api.post<{
      access_token: string;
      refresh_token: string;
      token_type: string;
      expires_in: number;
      user: User;
    }>('/api/auth/refresh', { refresh_token: refreshToken }),

  // 检查邮箱是否已注册
  checkEmail: (email: string) =>
    api.post<{ success: boolean; exists: boolean | null; message: string }>(
      '/api/auth/check-email',
      { email },
    ),

  // LinuxDo OAuth2
  checkLinuxDoEnabled: () =>
    api.get<{ enabled: boolean; client_id: string | null }>('/api/auth/linuxdo/enabled'),

  getLinuxDoAuthorizeUrl: () => api.get<{ authorize_url: string }>('/api/auth/linuxdo/authorize'),
};

// ==================== 分组 API ====================
export const groupApi = {
  // 获取分组列表
  list: () => api.get<ListResponse<SubscriptionGroup>>('/api/groups'),

  // 获取分组详情
  get: (id: string) => api.get<ApiResponse<SubscriptionGroup>>(`/api/groups/${id}`),

  // 创建分组
  create: (data: { name: string; description?: string; icon?: string; sort_order?: number }) =>
    api.post<ApiResponse<SubscriptionGroup>>('/api/groups', data),

  // 更新分组
  update: (
    id: string,
    data: { name?: string; description?: string; icon?: string; sort_order?: number },
  ) => api.put<ApiResponse<SubscriptionGroup>>(`/api/groups/${id}`, data),

  // 删除分组
  delete: (id: string) => api.delete<ApiResponse<unknown>>(`/api/groups/${id}`),
};

// ==================== 订阅 API ====================
export const subscriptionApi = {
  // 获取订阅列表
  list: (params?: { search?: string; group_id?: string; page?: number; page_size?: number }) =>
    api.get<ListResponse<Subscription>>('/api/subscriptions', { params }),

  // 获取启用的订阅
  getEnabled: () => api.get<ListResponse<Subscription>>('/api/subscriptions/enabled'),

  // 添加订阅
  create: (data: CreateSubscriptionRequest) =>
    api.post<ApiResponse<Subscription>>('/api/subscriptions', data),

  // 更新订阅
  update: (id: string, data: UpdateSubscriptionRequest) =>
    api.put<ApiResponse<Subscription>>(`/api/subscriptions/${id}`, data),

  // 批量更新
  batchUpdate: (data: BatchSubscriptionRequest) =>
    api.post<ApiResponse<unknown>>('/api/subscriptions/batch', data),

  // 同步订阅
  sync: (force?: boolean) =>
    api.post<ApiResponse<unknown>>('/api/subscriptions/sync', null, { params: { force } }),

  // 全部开启/关闭
  toggleAll: (enable: boolean) =>
    api.post<ApiResponse<unknown>>('/api/subscriptions/toggle-all', null, { params: { enable } }),
};

// ==================== 文章 API ====================
export const articleApi = {
  // 获取聚合文章列表（所有已启用订阅）
  getAll: (params?: {
    days?: number;
    page?: number;
    page_size?: number;
    group_id?: string;
    search?: string;
    is_read?: boolean;
    is_favorite?: boolean;
  }) => api.get<ApiResponse<FeedArticlesResponse>>('/api/articles', { params }),

  // 获取文章全文内容
  getContent: (id: string) =>
    api.get<ApiResponse<ArticleContentResponse>>(`/api/articles/${id}/content`),

  // 获取未读计数（总计 + 分组 + 阅读统计）
  getUnreadCounts: (days: number = 30) =>
    api.get<ApiResponse<UnreadCountsResponse>>('/api/articles/unread-counts', { params: { days } }),

  // 标记已读/未读
  toggleRead: (articleId: string, read: boolean) =>
    api.post<ApiResponse<unknown>>(`/api/articles/${articleId}/read`, { read }),

  // 收藏/取消收藏
  toggleFavorite: (articleId: string, favorite: boolean) =>
    api.post<ApiResponse<unknown>>(`/api/articles/${articleId}/favorite`, { favorite }),

  // 批量标记已读
  markAllRead: (scope: string = 'all', scopeId?: string) =>
    api.post<ApiResponse<{ updated: number }>>('/api/articles/read/mark-all', {
      scope,
      scope_id: scopeId,
    }),

  // 获取公众号历史文章
  getSubscriptionArticles: (
    subscriptionId: string,
    params?: { days?: number; page?: number; page_size?: number },
  ) =>
    api.get<ApiResponse<SubscriptionArticlesResponse>>(
      `/api/articles/subscription/${subscriptionId}`,
      { params },
    ),

  // 获取文章摘要
  getSummary: (link: string) =>
    api.get<ApiResponse<{ has_summary: boolean; summary?: string; summary_generated_at?: string }>>(
      '/api/articles/summary',
      { params: { link } },
    ),

  // 生成文章摘要
  generateSummary: (data: ArticleSummaryRequest) =>
    api.post<
      ApiResponse<{
        article_link: string;
        title: string;
        summary: string;
        summary_generated_at: string;
        is_cached: boolean;
      }>
    >('/api/articles/summary', data),

  // 批量获取摘要
  batchGetSummaries: (articleLinks: string[]) =>
    api.post<
      ApiResponse<
        Record<string, { has_summary: boolean; summary?: string; summary_generated_at?: string }>
      >
    >('/api/articles/summary/batch', { article_links: articleLinks }),

  // 获取订阅的摘要统计
  getSubscriptionSummaryStats: (subscriptionId: string) =>
    api.get<
      ApiResponse<{
        subscription_id: string;
        subscription_name: string;
        total_articles: number;
        summarized_articles: number;
        summary_rate: number;
      }>
    >(`/api/articles/subscription/${subscriptionId}/stats`),

  // 记录阅读时长
  logReadingTime: (
    articleId: string,
    data: { duration_seconds: number; started_at: string; ended_at: string },
  ) => api.post<ApiResponse<unknown>>(`/api/articles/${articleId}/reading-log`, data),

  // 保存阅读进度
  saveReadingProgress: (articleId: string, progress: number) =>
    api.put<ApiResponse<{ article_id: string; progress: number; last_read_at: string }>>(
      `/api/articles/${articleId}/progress`,
      { progress },
    ),

  // 获取阅读进度
  getReadingProgress: (articleId: string) =>
    api.get<ApiResponse<{ article_id: string; progress: number; last_read_at: string | null }>>(
      `/api/articles/${articleId}/progress`,
    ),

  // 获取阅读统计
  getReadingStats: (days?: number) =>
    api.get<
      ApiResponse<{
        total_reading_seconds: number;
        total_articles_read: number;
        avg_reading_seconds: number;
        daily_reading: Array<{ date: string; seconds: number; articles: number }>;
        subscription_distribution: Array<{
          subscription_id: string;
          subscription_name: string;
          articles: number;
          seconds: number;
        }>;
      }>
    >('/api/articles/reading-stats', { params: { days } }),

  // 获取热门趋势标签
  getTrendingTags: (days?: number, limit?: number) =>
    api.get<ApiResponse<TrendingTagsResponse>>('/api/articles/trending-tags', {
      params: { days: days || 7, limit: limit || 20 },
    }),

  // 获取内容类型统计
  getContentTypeStats: (days?: number) =>
    api.get<ApiResponse<ContentTypeStatsResponse>>('/api/articles/content-type-stats', {
      params: { days: days || 7 },
    }),

  // 获取所有标签（自由标签）
  getTags: (days?: number) =>
    api.get<ApiResponse<{ tags: Array<{ name: string; count: number }>; total: number }>>(
      '/api/articles/tags',
      { params: { days: days || 30 } },
    ),

  // 获取标签分类法（趋势标签 + 内容类型）
  getTagTaxonomy: () =>
    api.get<
      ApiResponse<{ trend_tags: string[]; content_types: { value: string; label: string }[] }>
    >('/api/articles/tag-taxonomy'),

  // 查找重复文章
  findDuplicates: (params?: { subscription_ids?: string; threshold?: number; days?: number }) =>
    api.get<ApiResponse<DuplicatesResponse>>('/api/articles/duplicates', { params }),

  // 标记重复文章
  markDuplicates: (data: MarkDuplicatesRequest) =>
    api.post<ApiResponse<MarkDuplicatesResponse>>('/api/articles/duplicates/mark', data),

  // 高级搜索文章
  advancedSearch: (params?: {
    q?: string;
    subscription_ids?: string;
    categories?: string;
    date_from?: string;
    date_to?: string;
    is_read?: boolean;
    is_starred?: boolean;
    sort_by?: string;
    sort_order?: string;
    page?: number;
    page_size?: number;
  }) => api.get<ApiResponse<FeedArticlesResponse>>('/api/articles/search', { params }),

  // 获取搜索建议
  getSearchSuggestions: (query: string, limit: number = 5) =>
    api.get<ApiResponse<{ suggestions: string[] }>>('/api/articles/search/suggestions', {
      params: { q: query, limit },
    }),
};

// ==================== Blocklist API ====================
export const blocklistApi = {
  // 获取屏蔽列表
  list: (params?: { kind?: string; page?: number; page_size?: number }) =>
    api.get<ListResponse<BlocklistEntry>>('/api/blocklist', { params }),

  // 添加屏蔽规则
  create: (data: CreateBlocklistRequest) =>
    api.post<ApiResponse<BlocklistEntry>>('/api/blocklist', data),

  // 更新屏蔽规则
  update: (id: string, data: UpdateBlocklistRequest) =>
    api.put<ApiResponse<BlocklistEntry>>(`/api/blocklist/${id}`, data),

  // 删除屏蔽规则
  delete: (id: string) => api.delete<ApiResponse<unknown>>(`/api/blocklist/${id}`),

  // 导出屏蔽列表
  export: () => api.get<ApiResponse<unknown[]>>('/api/blocklist/export'),

  // 导入屏蔽列表
  import: (items: unknown[]) => api.post<ApiResponse<unknown>>('/api/blocklist/import', items),
};

// ==================== 设置 API ====================
export const settingsApi = {
  // 获取用户设置
  get: () => api.get<ApiResponse<UserSettings>>('/api/settings'),

  // 更新用户设置
  update: (data: Partial<UserSettings>) =>
    api.put<ApiResponse<UserSettings>>('/api/settings', data),

  // 测试RSS连接
  testRss: () => api.get<ApiResponse<unknown>>('/api/settings/test-rss'),

  // 测试AI连接
  testAi: () => api.get<ApiResponse<unknown>>('/api/settings/test-ai'),
};

// ==================== Webhook API ====================
export const webhookApi = {
  // 获取webhook列表
  list: () => api.get<ListResponse<unknown>>('/api/webhooks'),

  // 添加webhook
  addWebhook: (data: CreateWebhookRequest) => api.post<ApiResponse<unknown>>('/api/webhooks', data),

  // 更新webhook
  updateWebhook: (id: string, data: UpdateWebhookRequest) =>
    api.put<ApiResponse<unknown>>(`/api/webhooks/${id}`, data),

  // 删除webhook
  deleteWebhook: (id: string) => api.delete<ApiResponse<unknown>>(`/api/webhooks/${id}`),

  // 测试webhook
  testWebhook: (id: string) => api.post<ApiResponse<WebhookTestResult>>(`/api/webhooks/${id}/test`),
};

// ==================== 报告 API ====================
export const reportApi = {
  // 获取报告列表
  list: (params?: { page?: number; page_size?: number }) =>
    api.get<ListResponse<Report>>('/api/reports', { params }),

  // 获取我的报告列表
  getMyReports: (limit?: number) =>
    api.get<ListResponse<UserReport>>('/api/reports', { params: { page_size: limit || 30 } }),

  // 获取今日报告
  getToday: () => api.get<ApiResponse<Report>>('/api/reports/today'),

  // 获取报告详情
  get: (id: string) => api.get<ApiResponse<Report>>(`/api/reports/${id}`),

  // 获取报告文章列表
  getArticles: (id: string) => api.get<ListResponse<ReportArticle>>(`/api/reports/${id}/articles`),

  // 生成报告
  generate: (data?: GenerateReportRequest) =>
    api.post<ApiResponse<Report>>('/api/reports/generate', data),

  // 删除报告
  delete: (id: string) => api.delete<ApiResponse<unknown>>(`/api/reports/${id}`),
};

// ==================== 统计 API ====================
export const statsApi = {
  // 获取仪表盘统计
  getDashboard: () => api.get<ApiResponse<DashboardStats>>('/api/stats/dashboard'),

  // 获取用户统计
  getUserStats: () => api.get<ApiResponse<UserStats>>('/api/stats/user'),
};

// ==================== 提示词管理 API ====================
export const promptsApi = {
  // 获取提示词列表
  list: (promptType?: string) =>
    api.get<ListResponse<Prompt>>('/api/prompts', { params: { prompt_type: promptType } }),

  // 获取系统提示词列表（管理员接口，但普通用户也可以查看系统提示词）
  listSystem: () => api.get<ListResponse<Prompt>>('/api/prompts', { params: { system: true } }),

  // 获取提示词类型
  getTypes: () => api.get<ApiResponse<PromptType[]>>('/api/prompts/types'),

  // 获取默认提示词
  getDefault: (promptType: string) =>
    api.get<ApiResponse<Prompt>>(`/api/prompts/default/${promptType}`),

  // 创建提示词
  create: (data: CreatePromptRequest) => api.post<ApiResponse<Prompt>>('/api/prompts', data),

  // 更新提示词
  update: (id: string, data: UpdatePromptRequest) =>
    api.put<ApiResponse<Prompt>>(`/api/prompts/${id}`, data),

  // 删除提示词
  delete: (id: string) => api.delete<ApiResponse<unknown>>(`/api/prompts/${id}`),

  // 重置为系统默认
  reset: (id: string) => api.post<ApiResponse<Prompt>>(`/api/prompts/${id}/reset`),

  // 复制系统提示词
  copy: (id: string) => api.post<ApiResponse<Prompt>>(`/api/prompts/${id}/copy`),

  // AI生成屏蔽标签
  generateBlockTags: (data: AIGenerateBlockRequest) =>
    api.post<AIGenerateBlockResponse>('/api/prompts/generate-block-tags', data),

  // 应用屏蔽标签
  applyBlockTags: (data: ApplyBlockTagsRequest) =>
    api.post<ApiResponse<unknown>>('/api/prompts/apply-block-tags', data),

  // 获取用户标签
  listUserTags: (category?: string) =>
    api.get<ListResponse<UserTag>>('/api/prompts/user-tags', {
      params: { tag_category: category },
    }),

  // 创建用户标签
  createUserTag: (data: CreateUserTagRequest) =>
    api.post<ApiResponse<UserTag>>('/api/prompts/user-tags', data),

  // 更新用户标签
  updateUserTag: (id: string, data: UpdateUserTagRequest) =>
    api.put<ApiResponse<UserTag>>(`/api/prompts/user-tags/${id}`, data),

  // 删除用户标签
  deleteUserTag: (id: string) => api.delete<ApiResponse<unknown>>(`/api/prompts/user-tags/${id}`),

  // 获取用于过滤的标签
  getTagsForFilter: () =>
    api.get<ApiResponse<TagsForFilterResponse>>('/api/prompts/user-tags/for-filter'),
};

// ==================== Feed 工具 API ====================
export const feedApi = {
  // 预览 feed
  preview: (data: FeedPreviewRequest) =>
    api.post<ApiResponse<FeedPreviewResponse>>('/api/feeds/preview', data),

  // 发现 feed
  discover: (data: FeedDiscoveryRequest) =>
    api.post<ApiResponse<FeedDiscoveryResponse>>('/api/feeds/discover', data),

  // 导入 OPML
  importOpml: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ApiResponse<OPMLImportResponse>>('/api/feeds/import-opml', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // 导出 OPML
  exportOpml: () => api.get('/api/feeds/export-opml', { responseType: 'blob' }),

  // 自动发现 feeds
  discoverFeeds: (data: DiscoverFeedsRequest) =>
    api.post<ApiResponse<DiscoverFeedsResponse>>('/api/feed-discovery/find-feeds', data),

  // 验证 feed
  validateFeed: (data: ValidateFeedRequest) =>
    api.post<ApiResponse<ValidateFeedResponse>>('/api/feed-discovery/validate-feed', data),
};

// ==================== 通知订阅 API ====================
type NotificationMutationPayload = {
  name?: string;
  channel?: string;
  channel_config?: Record<string, unknown> | null;
  cron_expression?: string;
  timezone?: string;
  subscription_filter?: string[] | null;
  tag_filter?: string[] | null;
  min_articles?: number;
  include_summary?: boolean;
  enabled?: boolean;
  [key: string]: unknown;
};

export const notificationApi = {
  // 获取所有通知订阅
  list: () => api.get<ApiResponse<NotificationSubscription[]>>('/api/notifications'),

  // 创建通知订阅
  create: (data: NotificationMutationPayload) =>
    api.post<ApiResponse<NotificationSubscription>>('/api/notifications', data),

  // 获取通知订阅详情
  get: (id: string) => api.get<ApiResponse<NotificationSubscription>>(`/api/notifications/${id}`),

  // 更新通知订阅
  update: (id: string, data: NotificationMutationPayload) =>
    api.put<ApiResponse<NotificationSubscription>>(`/api/notifications/${id}`, data),

  // 删除通知订阅
  delete: (id: string) => api.delete<ApiResponse<unknown>>(`/api/notifications/${id}`),

  // 预览通知内容
  preview: (
    id: string,
    hours?: number,
    options?: {
      report_month?: string;
      start_date?: string;
      end_date?: string;
    },
  ) =>
    api.post<ApiResponse<NotificationPreviewResponse>>(`/api/notifications/${id}/preview`, null, {
      params: { ...(hours ? { hours } : {}), ...(options || {}) },
    }),

  // 测试发送通知
  test: (id: string) =>
    api.post<ApiResponse<NotificationSubscription>>(`/api/notifications/${id}/test`),

  // 立即发送/历史补发
  send: (
    id: string,
    options?: {
      report_month?: string;
      start_date?: string;
      end_date?: string;
      mark_as_sent?: boolean;
    },
  ) =>
    api.post<ApiResponse<{ article_count: number; last_sent_updated: boolean }>>(
      `/api/notifications/${id}/send`,
      null,
      { params: options },
    ),

  // 获取通知模板列表
  listTemplates: () =>
    api.get<ApiResponse<NotificationTemplateItem[]>>('/api/notifications/templates/list'),

  // 获取模板详情
  getTemplate: (id: string) =>
    api.get<ApiResponse<NotificationTemplateItem>>(`/api/notifications/templates/${id}`),

  // 获取可用领域列表
  listDomains: () =>
    api.get<ApiResponse<NotificationDomainItem[]>>('/api/notifications/domains/list'),

  // 套用 wechat2rss/morning_report.py 标准配置
  getWechatAiMorningPreset: () =>
    api.get<ApiResponse<WechatAiMorningPreset>>('/api/notifications/presets/wechat-ai-morning'),

  // ============ 4 大支柱新端点 ============

  // Pillar 1：订阅源 picker 数据（按 folder 分组 + 近 N 小时文章数）
  sourcesPicker: (params?: { recent_hours?: number; notification_sub_id?: string }) =>
    api.get<
      ApiResponse<{
        recent_hours: number;
        groups: Array<{
          group_type: 'folder' | 'ungrouped';
          id: string | null;
          name: string;
          sources: Array<{
            subscription_id: string;
            name: string;
            alias: string | null;
            source_type: string;
            enabled: boolean;
            selected: boolean;
            recent_count: number;
            latest_published_at: string | null;
            priority_weight: number;
            icon_url: string | null;
            last_fetch_at: string | null;
            last_fetch_error: string | null;
            fetch_error_count: number;
            sync_status: 'synced' | 'error' | 'never_synced' | string;
            last_sync_at: string | null;
          }>;
        }>;
      }>
    >('/api/notifications/sources/picker', { params }),

  // Pillar 2：试跑筛选 prompt
  dryRunFilter: (
    id: string,
    body: {
      hours?: number;
      limit?: number;
      prompt?: string;
      preset_id?: string;
      fewshot?: Array<{ label: 'positive' | 'negative'; title: string; body: string }>;
      include_noise_check?: boolean;
      max_tokens?: number;
      temperature?: number;
    },
  ) =>
    api.post<
      ApiResponse<{
        hours: number;
        limit: number;
        total_scanned: number;
        results: Array<{
          article_id: string;
          title: string;
          published_at: string | null;
          stage: 'keyword' | 'ai_relevance';
          relevant: boolean | null;
          score: number | null;
          reason: string | null;
          tags: string[];
          noise: { is_noise: boolean; score: number; reason: string; tags: string[] } | null;
        }>;
      }>
    >(`/api/notifications/${id}/dry-run-filter`, body),

  // Pillar 3：试生成摘要
  dryRunSummary: (
    id: string,
    body: {
      article_id?: string;
      summary_prompt?: string;
      summary_max_tokens?: number;
      summary_tone?: string;
      summary_language?: string;
    },
  ) =>
    api.post<
      ApiResponse<{
        article_id: string;
        article_title: string;
        summary: string;
        settings: Record<string, unknown>;
        latency_ms: number;
      }>
    >(`/api/notifications/${id}/dry-run-summary`, body),

  // 列出系统 + 用户筛选/摘要 prompt 预设
  listFilterPresets: (prompt_type?: string) =>
    api.get<
      ApiResponse<
        Array<{
          id: string;
          name: string;
          description: string | null;
          prompt_type: string;
          content: string;
          fewshot_examples: Array<{ label: string; title: string; body: string }>;
          output_schema: Record<string, unknown> | null;
          max_tokens: number | null;
          temperature: number | null;
          is_system: boolean;
          is_default: boolean;
        }>
      >
    >('/api/notifications/presets/list', {
      params: prompt_type ? { prompt_type } : undefined,
    }),

  // 重发指定 log（gemini P3 修复）
  resendLog: (sub_id: string, log_id: string) =>
    api.post<
      ApiResponse<{
        log_id: string;
        status: 'success' | 'failed';
        channel: string;
        latency_ms: number;
        error_message: string | null;
      }>
    >(`/api/notifications/${sub_id}/send-logs/${log_id}/resend`),

  // Pillar 4：发送日志时间轴
  getSendLogs: (id: string, limit: number = 20) =>
    api.get<
      ApiResponse<
        Array<{
          id: string;
          action_type: 'scheduled' | 'test' | 'resend' | 'ops';
          channel: string;
          status: 'success' | 'failed' | 'skipped';
          attempt_no: number;
          max_attempts: number;
          retry_strategy: string;
          article_count: number;
          status_code: number | null;
          latency_ms: number | null;
          response_body: string | null;
          error_message: string | null;
          idempotency_key: string | null;
          created_at: string;
        }>
      >
    >(`/api/notifications/${id}/send-logs`, { params: { limit } }),
};

// ============ Draft 端点（向导阶段试跑/测试，无需先保存订阅） ============
export interface WechatAiMorningPreset {
  id: string;
  name: string;
  source_ids: string[];
  matched_sources: Array<Record<string, unknown>>;
  missing_sources: Array<Record<string, unknown>>;
  matched_count: number;
  missing_count: number;
  total_scope_count: number;
  template_id: string;
  template: Record<string, unknown>;
  feed_type: string;
  sort_by: string;
  max_articles: number;
  smart_distribution: boolean;
  ai_filters: Record<string, unknown>;
  filter_prompt: string;
  filter_fewshot: unknown[];
  summary_prompt: string;
  summary_max_tokens: number;
  summary_tone: string;
  summary_language: string;
}

export interface DraftSubscriptionPayload {
  name?: string;
  channel: string;
  channel_config?: Record<string, unknown>;
  template_id?: string;
  cron_expression?: string;
  timezone?: string;
  subscription_filter?: string[];
  tag_filter?: string[];
  domain_filter?: string[];
  feed_type?: string;
  ai_prompt?: string;
  ai_filters?: Record<string, unknown>;
  max_articles?: number;
  sort_by?: string;
  smart_distribution?: boolean;
  summary_prompt?: string;
  summary_max_tokens?: number;
  summary_tone?: string;
  summary_language?: string;
  retry_count?: number;
  retry_strategy?: string;
}

export const notificationDraftApi = {
  test: (body: DraftSubscriptionPayload & { article_limit?: number; idempotency_key?: string }) =>
    api.post<
      ApiResponse<{
        log_id: string | null;
        status: 'success' | 'failed';
        channel: string;
        latency_ms: number;
        attempt_no: number;
        max_attempts: number;
        article_count: number;
        error_message: string | null;
      }>
    >('/api/notifications/draft-test', body),

  dryRunFilter: (
    body: DraftSubscriptionPayload & {
      hours?: number;
      limit?: number;
      fewshot?: Array<{ label: string; title: string; body: string }>;
      preset_id?: string;
      include_noise_check?: boolean;
      max_tokens?: number;
      temperature?: number;
    },
  ) =>
    api.post<
      ApiResponse<{
        hours: number;
        limit: number;
        total_scanned: number;
        results: Array<{
          article_id: string;
          title: string;
          published_at: string | null;
          stage: 'keyword' | 'ai_relevance';
          relevant: boolean | null;
          score: number | null;
          reason: string | null;
          tags: string[];
          noise: { is_noise: boolean; score: number; reason: string; tags: string[] } | null;
        }>;
      }>
    >('/api/notifications/draft-dry-run-filter', body),

  dryRunSummary: (body: DraftSubscriptionPayload & { article_id?: string }) =>
    api.post<
      ApiResponse<{
        article_id: string;
        article_title: string;
        summary: string;
        settings: Record<string, unknown>;
        latency_ms: number;
      }>
    >('/api/notifications/draft-dry-run-summary', body),
};

// 补丁 ②：订阅源优先级 PATCH 走 /api/subscriptions
export const subscriptionPriorityApi = {
  setPriority: (subscription_id: string, priority_weight: number) =>
    api.patch<ApiResponse<{ subscription_id: string; priority_weight: number }>>(
      `/api/subscriptions/${subscription_id}/priority`,
      null,
      { params: { priority_weight } },
    ),
};

// ==================== 翻译 API ====================
export const translateApi = {
  // 翻译单个文本
  translate: (text: string, targetLang: string = 'en', sourceLang: string = 'auto') =>
    api.post<{
      success: boolean;
      translated_text: string;
      source_lang: string;
      detected_language: string;
      message?: string;
    }>('/api/translate', { text, target_lang: targetLang, source_lang: sourceLang }),

  // 批量翻译文本
  batchTranslate: (texts: string[], targetLang: string = 'en', sourceLang: string = 'auto') =>
    api.post<{
      success: boolean;
      translations: string[];
      source_lang: string;
      message?: string;
    }>('/api/translate/batch', { texts, target_lang: targetLang, source_lang: sourceLang }),
};

// ==================== 标签 API ====================
export const tagApi = {
  // 获取用户所有标签
  list: () => api.get<ApiResponse<TagsListResponse>>('/api/tags'),

  // 创建标签
  create: (data: CreateTagRequest) => api.post<ApiResponse<Tag>>('/api/tags', data),

  // 更新标签
  update: (tagId: string, data: UpdateTagRequest) =>
    api.put<ApiResponse<Tag>>(`/api/tags/${tagId}`, data),

  // 删除标签
  delete: (tagId: string) => api.delete<ApiResponse<{ id: string }>>(`/api/tags/${tagId}`),

  // 给文章添加标签
  addToArticle: (articleId: string, data: AddTagToArticleRequest) =>
    api.post<
      ApiResponse<{ article_id: string; tag_id: string; tag_name: string; tag_color: string }>
    >(`/api/tags/articles/${articleId}`, data),

  // 从文章移除标签
  removeFromArticle: (articleId: string, tagId: string) =>
    api.delete<ApiResponse<{ article_id: string; tag_id: string }>>(
      `/api/tags/articles/${articleId}/${tagId}`,
    ),

  // 获取文章的所有标签
  getArticleTags: (articleId: string) =>
    api.get<ApiResponse<ArticleTagsResponse>>(`/api/tags/articles/${articleId}`),

  // 按标签搜索文章
  searchArticles: (
    tagIds: string[],
    matchAll: boolean = false,
    page: number = 1,
    pageSize: number = 20,
  ) =>
    api.get<ApiResponse<SearchArticlesByTagsResponse>>('/api/tags/search', {
      params: {
        tag_ids: tagIds.join(','),
        match_all: matchAll,
        page,
        page_size: pageSize,
      },
    }),
};

// ==================== Notion 集成 API ====================
export const notionApi = {
  // 保存 API key 和数据库 ID
  connect: (data: { api_key: string; database_id: string }) =>
    api.post<ApiResponse<unknown>>('/api/integrations/notion/connect', data),

  // 测试连接
  testConnection: (data: { api_key: string }) =>
    api.post<ApiResponse<unknown>>('/api/integrations/notion/test', data),

  // 列出数据库
  listDatabases: () =>
    api.get<ApiResponse<{ databases: Array<{ id: string; title: string; url: string }> }>>(
      '/api/integrations/notion/databases',
    ),

  // 剪藏文章
  clipArticle: (articleId: string, includeContent: boolean = true) =>
    api.post<ApiResponse<{ page_url: string; page_id: string; article_title: string }>>(
      `/api/integrations/notion/articles/${articleId}/clip`,
      { include_content: includeContent },
    ),

  // 断开连接
  disconnect: () => api.delete<ApiResponse<unknown>>('/api/integrations/notion/disconnect'),

  // 获取集成状态
  getStatus: () =>
    api.get<
      ApiResponse<{
        enabled: boolean;
        has_api_key: boolean;
        has_database: boolean;
        database_id: string | null;
      }>
    >('/api/integrations/notion/status'),
};

// ==================== Agent Tokens ====================
export interface AgentToken {
  id: string;
  name: string;
  token_prefix: string;
  scope: string;
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
}

export interface AgentTokenCreated extends AgentToken {
  /** 明文 token，仅创建后本次响应可见 */
  token: string;
}

export interface AgentTokenListResponse {
  tokens: AgentToken[];
  total: number;
}

export const agentTokensApi = {
  list: (includeRevoked = false) =>
    api.get<AgentTokenListResponse>('/api/agent/tokens', {
      params: includeRevoked ? { include_revoked: true } : {},
    }),
  create: (name: string) => api.post<AgentTokenCreated>('/api/agent/tokens', { name }),
  revoke: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/api/agent/tokens/${id}`),
};

// ==================== 汇总导出 ====================
export const saasApi = {
  auth: authApi,
  groups: groupApi,
  subscriptions: subscriptionApi,
  articles: articleApi,
  blocklist: blocklistApi,
  settings: { ...settingsApi, ...webhookApi },
  reports: reportApi,
  stats: statsApi,
  webhooks: webhookApi,
  prompts: promptsApi,
  feeds: feedApi,
  notifications: notificationApi,
  translate: translateApi,
  tags: tagApi,
  notion: notionApi,
  agentTokens: agentTokensApi,
};

export default api;
